import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { KpiCard } from "@/lovable-demo/components/bits";
import {
  illustrativeIssuePrediction,
  predictionDirection,
} from "@/lovable-demo/lib/warranty/claims-prediction";
import { DEMO_OBSERVATION_MONTH } from "@/lovable-demo/lib/warranty/observation";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtCurrency, fmtNumber, recordNpr } from "@/lovable-demo/lib/warranty/calc";
import type { ProblemRecord } from "@/lovable-demo/lib/warranty/types";
import { CorrectiveActionPrediction } from "@/lovable-demo/components/corrective-action-prediction";

export function ClaimsScenario({ records }: { records: ProblemRecord[] }) {
  const [growth, setGrowth] = useState(5);
  const asOf = DEMO_OBSERVATION_MONTH;
  const rows = records
    .filter((r) => r.status !== "closed")
    .map((r) => illustrativeIssuePrediction(r, asOf, growth / 100));
  const months = rows[0]?.future.map((p) => p.month) ?? [];
  const baseline = rows.reduce((sum, row) => sum + Math.round(row.baseline), 0);
  const trend = [
    {
      month: "Assumed baseline",
      claims: Math.round(baseline),
      cost: rows.reduce((sum, row) => sum + row.baseline * row.averageCost, 0),
    },
    ...months.map((month, index) => ({
      month,
      claims: rows.reduce((sum, row) => sum + (row.future[index]?.claims ?? 0), 0),
      cost: rows.reduce((sum, row) => sum + (row.future[index]?.cost ?? 0), 0),
    })),
  ];
  const direction = predictionDirection(baseline, trend.at(-1)?.claims ?? 0);
  const tone =
    direction.label === "Predicted worse"
      ? "text-warning"
      : direction.label === "Predicted better"
        ? "text-success"
        : "text-muted-foreground";
  return (
    <div>
      <p className="mb-3 text-sm text-warning">
        Illustrative scenario · assumption-based prediction, not recorded claims or a validated
        forecast
      </p>
      <div className="mb-4 grid gap-4 border-l-2 border-primary pl-4 md:grid-cols-2">
        <p className="text-sm text-muted-foreground">
          Assumed starting monthly claims = record claim count ÷ 12, treating it as a 12-month total
          for this scenario only. Assumed cost per claim = record actual cost ÷ record claim count.
          No corrective-action benefit or seasonality is assumed; closed records are excluded.
        </p>
        <label className="text-sm">
          Assumed monthly claims change ·{" "}
          <span className="num font-semibold">
            {growth > 0 ? "+" : ""}
            {growth}%
          </span>
          <input
            aria-label="Assumed monthly claims change"
            type="range"
            min="-20"
            max="20"
            step="1"
            value={growth}
            onChange={(e) => setGrowth(Number(e.target.value))}
            className="mt-3 block w-full accent-primary"
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            Compounded monthly; starting assumption +5%, not inferred from evidence.
          </span>
        </label>
      </div>
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <KpiCard
          label="Scenario predicted claims · 6 months"
          value={fmtNumber(rows.reduce((s, p) => s + p.predictedClaims, 0))}
          hint={`${months[0] ?? "—"} – ${months.at(-1) ?? "—"}`}
        />
        <KpiCard
          label="Scenario estimated future cost"
          value={fmtCurrency(rows.reduce((s, p) => s + p.predictedCost, 0))}
          hint="Assumed claim cost · not actual spending"
        />
        <KpiCard
          label="Open issues in scenario"
          value={String(rows.length)}
          hint="Recorded exposure remains unchanged"
        />
      </div>
      <section
        className="mb-5 border-y border-border py-4"
        aria-label="Lingering issues prediction trend"
      >
        <div className="mb-3 flex flex-wrap justify-between gap-3">
          <div>
            <h3 className={`text-base font-semibold ${tone}`}>
              {direction.label} · lingering issues
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {direction.changePct === null
                ? "No claim baseline to assess direction."
                : `${direction.changePct > 0 ? "+" : ""}${direction.changePct.toFixed(1)}% monthly claims in month 6 versus assumed starting baseline.`}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            {rows.length} issues remain open · closure is not predicted
          </p>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">
              Monthly predicted claims
            </h4>
            <div
              className="h-56 min-w-0"
              role="img"
              aria-label="Predicted monthly claims from assumed baseline through month six"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} width={45} />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="rounded border border-border bg-popover p-2 text-xs text-popover-foreground">
                          {label} · {fmtNumber(Number(payload[0]?.value ?? 0))} claims
                        </div>
                      ) : null
                    }
                  />
                  <Line
                    dataKey="claims"
                    type="linear"
                    stroke="var(--chart-1)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div>
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">
              Monthly estimated claim cost
            </h4>
            <div
              className="h-56 min-w-0"
              role="img"
              aria-label="Estimated monthly claim cost from assumed baseline through month six"
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 10 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                    tickFormatter={(value: number) => `${(value / 1000000).toFixed(1)}M`}
                    width={45}
                  />
                  <Tooltip
                    content={({ active, payload, label }) =>
                      active && payload?.length ? (
                        <div className="rounded border border-border bg-popover p-2 text-xs text-popover-foreground">
                          {label} · {fmtCurrency(Number(payload[0]?.value ?? 0))}
                        </div>
                      ) : null
                    }
                  />
                  <Line
                    dataKey="cost"
                    type="linear"
                    stroke="var(--chart-2)"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Direction follows the assumed monthly change, not measured improvement. Falling claims do
          not verify corrective-action effectiveness or close issues; cumulative predicted cost
          continues to accumulate.
        </p>
      </section>
      <CorrectiveActionPrediction records={records} asOf={asOf} growth={growth / 100} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-left text-xs">
          <thead className="border-y border-border text-muted-foreground">
            <tr>
              {[
                "Issue",
                "Predicted direction",
                "Assumed claims / month",
                ...months,
                "6-month claims",
                "Estimated future cost",
                "Remaining exposure",
              ].map((s) => (
                <th key={s} className="px-2 py-3 font-medium">
                  {s}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((p) => (
              <tr key={p.record.id} className="align-top">
                <td className="max-w-64 px-2 py-3">
                  <Link
                    to="/demo/problem-record/$id"
                    params={{ id: p.record.id }}
                    className="text-primary hover:underline"
                  >
                    {p.record.code} · {p.record.title}
                  </Link>
                </td>
                <td className="px-2 py-3">
                  {predictionDirection(Math.round(p.baseline), p.future.at(-1)?.claims ?? 0).label}
                </td>
                <td className="num px-2 py-3">{fmtNumber(p.baseline, 1)}</td>
                {p.future.map((point) => (
                  <td key={point.month} className="num px-2 py-3">
                    {fmtNumber(point.claims)}
                  </td>
                ))}
                <td className="num px-2 py-3 font-semibold">{fmtNumber(p.predictedClaims)}</td>
                <td className="num whitespace-nowrap px-2 py-3">{fmtCurrency(p.predictedCost)}</td>
                <td className="num whitespace-nowrap px-2 py-3">
                  {fmtCurrency(recordNpr(p.record))}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="border-t border-border font-semibold">
              <tr>
                <td colSpan={3} className="px-2 py-3">
                  Scenario total
                </td>
                {months.map((month, index) => (
                  <td key={month} className="num px-2 py-3">
                    {fmtNumber(rows.reduce((sum, r) => sum + (r.future[index]?.claims ?? 0), 0))}
                  </td>
                ))}
                <td className="num px-2 py-3">
                  {fmtNumber(rows.reduce((sum, r) => sum + r.predictedClaims, 0))}
                </td>
                <td className="num px-2 py-3">
                  {fmtCurrency(rows.reduce((sum, r) => sum + r.predictedCost, 0))}
                </td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        Estimated costs may already be included in remaining exposure; do not add the two. Scenario
        changes are not saved as evidence, decisions, claims or approved financial assumptions.
      </p>
    </div>
  );
}
